import React, { Fragment, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import OnboardingRibbon from '../features/home/OnboardingRibbon';
import {
  Home,
  Clock,
  Calendar,
  CalendarClock,
  CalendarDays,
  BookOpen,
  HelpCircle,
  Ticket,
  Users,
  CreditCard,
  Wallet,
  FileText,
  Shield,
  Wrench,
  ClipboardCheck,
  Tag,
  TrendingUp,
  Building2,
  Cog,
  BarChart3,
  ClipboardList,
  Layers,
  Package,
  Boxes,
  LayoutGrid,
  Receipt,
  Inbox,
  ChevronRight,
  ChevronLeft,
  MapPin,
  Camera,
  Table2,
  Trophy,
  Search,
  Pin,
  PinOff,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MOBILE_BREAKPOINT_PX, useIsMobile } from '../hooks/useIsMobile';
import { MODULES } from '../constants/modules';
import { useBookings } from '../hooks/booking/useBookingContextSlices';
import { useBookingApprovals } from '../hooks/booking/useBookingApprovals';
import AppHeader from './AppHeader';
import Logo from './Logo';
import ErrorBoundary from './ErrorBoundary';
import BottomTabBar from './BottomTabBar';
import MoreSheet from './MoreSheet';

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= MOBILE_BREAKPOINT_PX : true
  );
  const { isSuperAdmin, hasPermission } = useAuth();
  const { bookings } = useBookings();
  const { pendingRequests, count: pendingRequestCount } = useBookingApprovals({ filter: 'pending', pollPending: true });

  const pendingPaymentsCount = useMemo(() => {
    if (!bookings) return 0;
    return bookings.filter(
      (b) => b.paymentStatus === 'pending' && b.status !== 'cancelled'
    ).length;
  }, [bookings]);

  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed_groups');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('sidebar_collapsed_groups', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Collapsed-to-rail state (desktop only), persisted.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_rail_collapsed') === 'true'; } catch { return false; }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar_rail_collapsed', String(next)); } catch {}
      // Clear any active filter when collapsing so the rail never hides icons
      // behind a leftover query the user can't see or clear.
      if (next) setNavFilter('');
      return next;
    });
  }, []);

  // Pinned nav paths, persisted.
  const [pinnedPaths, setPinnedPaths] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_pinned_v1');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const togglePin = useCallback((path) => {
    setPinnedPaths((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      try { localStorage.setItem('sidebar_pinned_v1', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Inline nav filter.
  const [navFilter, setNavFilter] = useState('');

  // Mobile bottom-tab navigation (UI-02 / #50). Facility users get the bottom
  // tab bar + More sheet on mobile; desktop keeps the sidebar, and super admin
  // (a distinct area) keeps the mobile drawer.
  const [moreOpen, setMoreOpen] = useState(false);
  const useBottomNav = isMobile && !isSuperAdmin;

  // Close the More sheet whenever we navigate or leave mobile.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);
  useEffect(() => { if (!useBottomNav) setMoreOpen(false); }, [useBottomNav]);

  const touchStartX = useRef(null);
  const handleSidebarTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);
  const handleSidebarTouchEnd = useCallback((e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta < -50 && isMobile) setSidebarOpen(false);
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
      setSidebarOpen((prev) => (mobile ? false : prev || true));
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const superAdminMenu = useMemo(() => [
    { section: 'PLATFORM', items: [
      { path: '/superadmin/facilities', label: 'Facilities', icon: Building2, title: 'Manage facilities' },
      { path: '/superadmin/users', label: 'Users', icon: Users, title: 'Manage users' },
      { path: '/superadmin/roles', label: 'Roles', icon: Shield, title: 'Manage roles & permissions' },
      { path: '/superadmin/config', label: 'Platform', icon: Cog, title: 'Platform configuration' },
      { path: '/superadmin/analytics/logins', label: 'Login analytics', icon: BarChart3, title: 'Login analytics' },
      { path: '/superadmin/analytics/audit', label: 'Audit log', icon: ClipboardList, title: 'Security audit log' },
      { path: '/superadmin/survey-responses', label: 'Survey responses', icon: ClipboardList, title: 'Early access survey submissions' },
      { path: '/superadmin/subscriptions', label: 'Subscriptions', icon: CreditCard, title: 'Facility license & subscription management' },
      { path: '/superadmin/facility-location', label: 'Ground location', icon: MapPin, title: 'Update facility GPS coordinates' },
    ]},
  ], []);

  const facilityMenu = useMemo(() => {
    const groups = [];

    // Dashboard — top level, always visible, no group
    if (hasPermission(MODULES.DASHBOARD)) {
      groups.push({
        section: null,
        key: '_dashboard',
        items: [{ path: '/dashboard', label: 'Home', icon: Home, title: 'Home' }],
      });
    }

    // Operations
    const operations = [];
    if (hasPermission(MODULES.BOOKINGS)) {
      operations.push({ path: '/dashboard/book-now', label: 'Booking calendar', icon: Calendar, title: 'Booking calendar' });
      operations.push({ path: '/dashboard/my-bookings', label: 'My bookings', icon: BookOpen, title: 'My bookings' });
      operations.push({ path: '/dashboard/booking-requests', label: 'Booking requests', icon: Inbox, title: 'Booking requests', badge: pendingRequestCount > 0 ? pendingRequestCount : null });
      operations.push({ path: '/dashboard/waitlist', label: 'Waitlist', icon: Clock, title: 'Waitlist bookings' });
    }
    if (hasPermission(MODULES.TOURNAMENTS)) {
      operations.push({
        path: '/dashboard/tournaments',
        label: 'Tournaments',
        icon: Trophy,
        title: 'Tournament registrations',
      });
    }
    if (hasPermission(MODULES.INDIVIDUAL_BOOKING)) {
      operations.push({
        path: '/dashboard/booking-new',
        label: 'Individual booking',
        icon: CalendarClock,
        title: 'Individual slot booking',
      });
    }
    if (hasPermission(MODULES.PAYMENTS)) {
      operations.push({
        path: '/dashboard/payments',
        label: 'Payments',
        icon: CreditCard,
        title: 'Payments management',
        badge: pendingPaymentsCount > 0 ? pendingPaymentsCount : null,
      });
    }
    if (hasPermission(MODULES.EXPENSES)) {
      operations.push({ path: '/dashboard/expenses', label: 'Expenses', icon: Receipt, title: 'Expenses' });
      operations.push({ path: '/dashboard/expenses/analytics', label: 'Expense analytics', icon: TrendingUp, title: 'Expense analytics' });
    }
    if (hasPermission(MODULES.OPERATIONS)) {
      operations.push({
        path: '/dashboard/operations/daily',
        label: 'Daily ops',
        icon: CalendarDays,
        title: 'Daily operations log',
      });
    }
    if (hasPermission(MODULES.CONFIGURATION, 'write')) {
      operations.push({
        path: '/dashboard/facility-services',
        label: 'Facility Services',
        icon: Building2,
        title: 'Sport and court service configuration',
      });
    }
    if (hasPermission(MODULES.REPORTS)) {
      operations.push({ path: '/dashboard/reports', label: 'Reports', icon: FileText, title: 'Reports' });
    }
    if (operations.length) {
      groups.push({ section: 'OPERATIONS', key: 'operations', items: operations });
    }

    // Asset management
    const assets = [];
    if (hasPermission(MODULES.EQUIPMENT)) {
      assets.push({
        path: '/dashboard/assets',
        label: 'Overview',
        icon: LayoutGrid,
        title: 'Asset management overview',
      });
      assets.push({
        path: '/dashboard/assets/fixed',
        label: 'Fixed Assets',
        icon: Package,
        title: 'Fixed assets and maintenance',
      });
      assets.push({
        path: '/dashboard/assets/inventory',
        label: 'Inventory',
        icon: Boxes,
        title: 'Consumables and stock',
      });
    }
    if (assets.length) {
      groups.push({ section: 'ASSET MANAGEMENT', key: 'assets', prominent: true, items: assets });
    }

    // Attendance
    const attendance = [];
    if (hasPermission(MODULES.STAFF)) {
      attendance.push({
        path: '/dashboard/staff/attendance',
        label: 'Attendance',
        icon: ClipboardCheck,
        title: 'Staff attendance',
      });
    }
    if (hasPermission(MODULES.SELFIE_ATTENDANCE)) {
      attendance.push({
        path: '/dashboard/selfie-attendance',
        label: 'Selfie attendance',
        icon: Camera,
        title: 'Selfie-based attendance',
      });
      attendance.push({
        path: '/dashboard/selfie-attendance/records',
        label: 'Selfie records',
        icon: Table2,
        title: 'All selfie attendance (date, IN/OUT, hours)',
      });
      attendance.push({
        path: '/dashboard/selfie-attendance/history',
        label: 'Selfie history',
        icon: Clock,
        title: 'Per-staff selfie history',
      });
    }
    if (attendance.length) {
      groups.push({ section: 'ATTENDANCE', key: 'attendance', items: attendance });
    }

    // People
    const people = [];
    if (hasPermission(MODULES.STAFF)) {
      people.push({ path: '/dashboard/staff', label: 'Staff', icon: Users, title: 'Staff management' });
      people.push({ path: '/dashboard/staff/salary', label: 'Salary', icon: Wallet, title: 'Staff salary' });
    }
    if (hasPermission(MODULES.ROLES) || hasPermission(MODULES.USERS)) {
      people.push({ path: '/dashboard/roles', label: 'Roles & access', icon: Shield, title: 'Roles & permissions' });
    }
    if (people.length) {
      groups.push({ section: 'PEOPLE', key: 'people', items: people });
    }

    // Settings
    const settings = [];
    if (hasPermission(MODULES.USERS)) {
      settings.push({ path: '/dashboard/users', label: 'Users', icon: Users, title: 'Users' });
    }
    // "Configure service" removed: Facility Services now handles add + per-service
    // timings/pricing + edit, so the legacy configure-service wizard is redundant.
    if (hasPermission(MODULES.CONFIGURATION, 'write')) {
      settings.push({
        path: '/dashboard/facility-location',
        label: 'Ground location',
        icon: MapPin,
        title: 'Update facility GPS coordinates',
      });
    }
    if (hasPermission(MODULES.EXPENSES)) {
      settings.push({ path: '/dashboard/expenses/categories', label: 'Expense categories', icon: Tag, title: 'Expense categories' });
    }
    if (settings.length) {
      groups.push({ section: 'SETTINGS', key: 'settings', items: settings });
    }

    // Support
    const support = [];
    if (hasPermission(MODULES.HELP_CENTER)) {
      support.push({ path: '/dashboard/help-center', label: 'Help center', icon: HelpCircle, title: 'Help center' });
      support.push({ path: '/dashboard/create-ticket', label: 'Create ticket', icon: Ticket, title: 'Create support ticket' });
    }
    if (support.length) {
      groups.push({ section: 'SUPPORT', key: 'support', items: support });
    }

    return groups;
  }, [hasPermission, pendingPaymentsCount, pendingRequestCount]);

  const menuSections = isSuperAdmin ? superAdminMenu : facilityMenu;

  // Active nav = the single nav item whose path is the *longest* prefix match
  // for the current pathname. This gives exact-match behaviour for leaf routes
  // (e.g. "Inventory" at /dashboard/operations no longer lights up on
  // /dashboard/operations/equipment) while still keeping a parent item active
  // on its own detail sub-routes when no more specific item exists.
  const allNavPaths = useMemo(
    () => menuSections.flatMap((group) => group.items.map((item) => item.path)),
    [menuSections]
  );

  const activeNavPath = useMemo(() => {
    const current = location.pathname;
    let best = null;
    for (const path of allNavPaths) {
      if (current === path || current.startsWith(path + '/')) {
        if (best === null || path.length > best.length) best = path;
      }
    }
    return best;
  }, [allNavPaths, location.pathname]);

  const isActive = useCallback((path) => path === activeNavPath, [activeNavPath]);

  // Rail (icon-only) mode only applies on desktop with the sidebar open.
  const rail = railCollapsed && !isMobile && sidebarOpen;

  const itemByPath = useMemo(() => {
    const map = {};
    menuSections.forEach((group) => group.items.forEach((item) => { map[item.path] = item; }));
    return map;
  }, [menuSections]);

  const pinnedItems = useMemo(
    () => pinnedPaths.map((p) => itemByPath[p]).filter(Boolean),
    [pinnedPaths, itemByPath]
  );

  // Filtering never applies in rail mode (no input is shown there).
  const query = rail ? '' : navFilter.trim().toLowerCase();
  const matchesQuery = useCallback(
    (item) => !query || item.label.toLowerCase().includes(query) || (item.title || '').toLowerCase().includes(query),
    [query]
  );

  const renderNavItem = (item, pinned = false) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <div key={`${pinned ? 'pin-' : ''}${item.path}`} className={`nav-row ${active ? 'active' : ''}`}>
        <Link
          to={item.path}
          title={item.title ?? item.label}
          onClick={handleNavClick}
          aria-current={active ? 'page' : undefined}
          className="nav-item"
        >
          <Icon className="nav-icon" aria-hidden />
          <span className="nav-label leading-snug break-words min-w-0">{item.label}</span>
          {item.badge != null && <span className="sidebar-badge">{item.badge}</span>}
        </Link>
        {!rail && (
          <button
            type="button"
            className={`nav-pin ${pinned ? 'pinned' : ''}`}
            aria-label={pinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
            title={pinned ? 'Unpin' : 'Pin to top'}
            onClick={() => togglePin(item.path)}
          >
            {pinned ? <PinOff className="h-3.5 w-3.5" aria-hidden /> : <Pin className="h-3.5 w-3.5" aria-hidden />}
          </button>
        )}
      </div>
    );
  };

  const hasAnyMatch = menuSections.some((group) => group.items.some(matchesQuery));

  return (
    <div className="min-h-screen bg-ga-bg-page overflow-x-hidden">
      {isMobile && sidebarOpen && !useBottomNav && (
        <button
          type="button"
          aria-label="Close sidebar"
          className="fixed inset-0 z-30 bg-black/40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <AppHeader
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
        isDesktopSidebarOpen={!isMobile && sidebarOpen}
        showSidebarToggle={!useBottomNav}
        pendingRequestCount={pendingRequestCount}
        pendingRequests={pendingRequests}
      />

      <div className="flex min-h-[calc(100vh-52px-env(safe-area-inset-top,0px))] pt-[calc(52px+env(safe-area-inset-top,0px))] relative">
        {!useBottomNav && (
        <aside
          className={`ga-dashboard-sidebar fixed left-0 top-[calc(52px+env(safe-area-inset-top,0px))] bottom-0 z-40 min-h-0 ${rail ? 'rail' : ''} ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          onTouchStart={handleSidebarTouchStart}
          onTouchEnd={handleSidebarTouchEnd}
        >
          <div className="sidebar-head">
            {!rail && (
              <Link
                to={isSuperAdmin ? '/superadmin/facilities' : '/dashboard'}
                className="sidebar-logo shrink-0"
                title="Home"
                onClick={handleNavClick}
              >
                <Logo className="min-w-0 [&_img]:h-7 [&_img]:w-auto" />
              </Link>
            )}
            {!isMobile && (
              <button
                type="button"
                className="sidebar-collapse-btn"
                onClick={toggleRail}
                aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
                title={rail ? 'Expand' : 'Collapse'}
              >
                {rail ? <ChevronRight className="h-4 w-4" aria-hidden /> : <ChevronLeft className="h-4 w-4" aria-hidden />}
              </button>
            )}
          </div>

          {!rail && (
            <div className="sidebar-search">
              <div className="sidebar-search-field">
                <Search className="sidebar-search-icon" aria-hidden />
                <input
                  type="text"
                  value={navFilter}
                  onChange={(e) => setNavFilter(e.target.value)}
                  placeholder="Filter menu…"
                  aria-label="Filter navigation"
                  spellCheck={false}
                />
              </div>
            </div>
          )}

          <div className="sidebar-scroll">
            {!rail && !query && pinnedItems.length > 0 && (
              <>
                <div className="section-label">PINNED</div>
                {pinnedItems.map((item) => renderNavItem(item, true))}
                <div className="sidebar-divider" aria-hidden />
              </>
            )}

            {menuSections.map((group, idx) => {
              const isCollapsible = !!group.section;
              const groupKey = group.key || group.section;
              const items = query ? group.items.filter(matchesQuery) : group.items;
              if (items.length === 0) return null;
              // While filtering, force groups open so matches are visible.
              const isOpen = query ? true : !collapsedGroups[groupKey];

              return (
                <Fragment key={groupKey}>
                  {idx > 0 && (
                    <div
                      className={`sidebar-divider${group.prominent ? ' sidebar-divider--prominent' : ''}`}
                      aria-hidden
                    />
                  )}

                  {isCollapsible && !rail ? (
                    <>
                      <button
                        type="button"
                        className={`sidebar-group-header${group.prominent ? ' sidebar-group-header--prominent' : ''}`}
                        onClick={() => toggleGroup(groupKey)}
                        aria-expanded={isOpen}
                      >
                        <ChevronRight
                          className={`sidebar-group-chevron ${isOpen ? 'sidebar-group-chevron--open' : ''}`}
                          aria-hidden
                        />
                        <span>{group.section}</span>
                      </button>
                      <div
                        className="sidebar-group-items"
                        style={{ maxHeight: isOpen ? `${items.length * 52}px` : '0px' }}
                      >
                        {items.map((item) => renderNavItem(item, false))}
                      </div>
                    </>
                  ) : (
                    items.map((item) => renderNavItem(item, false))
                  )}
                </Fragment>
              );
            })}

            {!rail && query && !hasAnyMatch && (
              <p className="sidebar-empty">No menu items match "{navFilter}".</p>
            )}
          </div>
        </aside>
        )}

        <div
          className={`flex-1 flex flex-col min-w-0 w-full transition-[padding-left] duration-300 ${
            !isMobile && sidebarOpen ? (rail ? 'pl-14' : 'pl-64') : 'pl-0'
          }`}
        >
          <main className="flex-1 bg-ga-bg-page p-3 md:p-6 overflow-y-auto overflow-x-hidden min-h-0 min-w-0">
            <ErrorBoundary>
              {new URLSearchParams(location.search).get('onboarding') === '1' && <OnboardingRibbon />}
              {children}
            </ErrorBoundary>
            {useBottomNav && <div className="ga-bottom-tabs-spacer" aria-hidden />}
          </main>
        </div>
      </div>

      {useBottomNav && (
        <>
          <BottomTabBar
            onOpenMore={() => setMoreOpen(true)}
            moreOpen={moreOpen}
            pendingPaymentsCount={pendingPaymentsCount}
            pendingRequestCount={pendingRequestCount}
            showTournaments={hasPermission(MODULES.TOURNAMENTS)}
          />
          <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
        </>
      )}
    </div>
  );
};

export default DashboardLayout;
